import { useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Home, Compass, BookOpen, BarChart3, Settings } from "lucide-react";
import { SkipLink } from "../components/ui";
import { useApp, useTenant } from "../context";
import { PublicSupport } from "../components/PublicSupport";
const NAV_ITEMS = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/practice", icon: Compass, label: "Practice" },
  { to: "/explore", icon: BookOpen, label: "Explore" },
  { to: "/progress", icon: BarChart3, label: "Progress" },
];
export function AppShell() {
  const { state } = useApp();
  const { brand } = useTenant();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const { theme, fontSize, highContrast, enhancedFocus, reducedMotion } =
      state.settings;
    const html = document.documentElement;
    if (theme !== "system" && theme !== "dark")
      document.body.setAttribute("data-theme", theme);
    else document.body.removeAttribute("data-theme");
    if (fontSize !== "default") html.setAttribute("data-font-size", fontSize);
    else html.removeAttribute("data-font-size");
    if (highContrast) html.setAttribute("data-high-contrast", "true");
    else html.removeAttribute("data-high-contrast");
    if (enhancedFocus) html.setAttribute("data-enhanced-focus", "true");
    else html.removeAttribute("data-enhanced-focus");
    html.toggleAttribute("data-reduced-motion", reducedMotion);
    return () => {
      document.body.removeAttribute("data-theme");
      for (const attr of [
        "data-font-size",
        "data-high-contrast",
        "data-enhanced-focus",
        "data-reduced-motion",
      ])
        html.removeAttribute(attr);
    };
  }, [
    state.settings.theme,
    state.settings.fontSize,
    state.settings.highContrast,
    state.settings.enhancedFocus,
    state.settings.reducedMotion,
  ]);
  useLayoutEffect(() => {
    window.scrollTo?.(0, 0);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);
  return (
    <div className="calm-shell">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <header className="calm-header">
        <Link to="/" className="calm-brand">
          <Compass size={24} aria-hidden="true" />
          {brand.name}
        </Link>
        <div className="calm-header-actions">
          <PublicSupport />
          <NavLink
            to="/settings"
            className="settings-link"
            aria-label="Settings"
          >
            <Settings size={20} aria-hidden="true" />
          </NavLink>
        </div>
      </header>
      <main ref={mainRef} id="main-content" tabIndex={-1} className="calm-main">
        <Outlet />
      </main>
      <nav aria-label="Main navigation" className="calm-nav">
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `calm-nav-link${isActive ? " is-active" : ""}`
            }
          >
            <Icon size={21} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
